/**
 * New-version prompt. A running tab keeps its old JavaScript until the next
 * navigation, and the service worker file never changes, so nothing tells the
 * player a newer build is live. This re-reads the served index page and, when
 * its hashed entry script differs from the one this tab runs, shows a bar with
 * a Reload button. It never reloads by itself, never touches saves or caches,
 * and the player chooses when to reload (so queued commands are not cut off).
 */

/** Hashed entry script name from index.html, or null for dev/unhashed pages. */
export function entryScriptOf(html: string): string | null {
  const m = /<script[^>]*type="module"[^>]*src="([^"]*assets\/[^"]+\.js)"/i.exec(html)
    ?? /<script[^>]*src="([^"]*assets\/[^"]+\.js)"[^>]*type="module"/i.exec(html);
  return m?.[1]?.split('/').pop() ?? null;
}

export function isNewerBuild(running: string | null, served: string | null): boolean {
  return running !== null && served !== null && running !== served;
}

const BAR_ID = 'ea-update-bar';

function showBar(): void {
  if (document.getElementById(BAR_ID)) return;
  const bar = document.createElement('div');
  bar.id = BAR_ID;
  bar.setAttribute('role', 'status');
  bar.style.cssText = 'position:fixed;left:8px;right:8px;top:calc(env(safe-area-inset-top,0px) + 58px);z-index:9999;display:flex;gap:8px;align-items:center;justify-content:space-between;padding:10px 12px;border-radius:12px;background:#2a2016;color:#f3e8d2;border:1px solid #c88a2e;font:14px system-ui,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,.4)';
  const msg = document.createElement('span');
  msg.textContent = 'A new version is ready. Your save is kept.';
  const go = document.createElement('button');
  go.textContent = 'Reload';
  go.style.cssText = 'padding:8px 14px;border-radius:8px;border:0;background:#c88a2e;color:#1a140d;font-weight:700';
  go.onclick = () => { location.reload(); };
  const later = document.createElement('button');
  later.textContent = 'Later';
  later.style.cssText = 'padding:8px 10px;border-radius:8px;border:1px solid #6b5a3e;background:transparent;color:#f3e8d2';
  later.onclick = () => { bar.remove(); };
  const btns = document.createElement('span');
  btns.style.cssText = 'display:flex;gap:6px';
  btns.append(later, go);
  bar.append(msg, btns);
  document.body.appendChild(bar);
}

export function startUpdateWatch(intervalMs = 5 * 60_000): void {
  if (typeof document === 'undefined' || (location.protocol !== 'https:' && location.hostname !== 'localhost')) return;
  const running = entryScriptOf(document.documentElement.outerHTML);
  if (running === null) return; // dev build
  let busy = false;
  const check = async (): Promise<void> => {
    if (busy || document.getElementById(BAR_ID)) return;
    busy = true;
    try {
      const res = await fetch(`${location.pathname}?_v=${String(Date.now())}`, { cache: 'no-store' });
      if (res.ok && isNewerBuild(running, entryScriptOf(await res.text()))) showBar();
    } catch { /* offline: try again later */ } finally { busy = false; }
  };
  setInterval(() => { void check(); }, intervalMs);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void check(); });
}
